/**
 * Isolated axios client for the SuperAdmin panel.
 * Uses its OWN `admin_token` localStorage key so the regular user session
 * (`token`) and admin session cannot mix. Never installed as axios default
 * header — only this instance carries the admin bearer.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ADMIN_TOKEN_KEY = 'admin_token';

export const adminApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Detail strings the backend returns specifically for JWT / session-level
// failures. Any 401 whose detail does NOT match one of these is treated as
// a per-request validation rejection (wrong password, bogus OTP, …) and
// must NOT wipe the admin session — otherwise the first failed
// password / OTP entry inside a change-X dialog silently logs the
// SuperAdmin out.
const TOKEN_FAIL_DETAILS = new Set([
  'Admin authentication required',
  'Admin session expired',
  'Invalid admin token',
  'User not found',
]);

function isSessionLevel401(error) {
  if (error?.response?.status !== 401) return false;
  const detail = error.response.data?.detail;
  if (typeof detail !== 'string') return false;
  return TOKEN_FAIL_DETAILS.has(detail);
}

adminApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (isSessionLevel401(error)) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
    return Promise.reject(error);
  }
);

export function adminLogout() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}
